/* eslint-disable max-lines */
import { useAuth0 } from '@auth0/auth0-react';
import { IconNames } from '@blueprintjs/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { ChangeRequestSubType, RequestType, RequestTypeDisplayTextMap } from '@strata/react-chat';
import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { DialogWithForm } from '@strata/react-dialog';
import { FileUploadFormik } from '@strata/react-file-upload';
import { Hyperlink } from '@strata/react-hyperlink';
import { InlineMessage } from '@strata/react-inline-message';
import { TextAreaFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { Icon } from '@strata/react-status-icons';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import * as Yup from 'yup';

import { PBMOnlySelector } from 'components/requests/dialogs//PBMOnlySelector';
import { MemberEscalation } from 'components/requests/dialogs/MemberEscalation';
import { ReadPermissionRequestTypeMap } from 'services/constants';
import { type RequestDetails } from 'services/interfaces';
import { AppRoutes } from 'services/routes';
import { useAppSelector } from 'store';
import {
    useGetCompassUserNameMapQuery,
    useGetPayerUserWithPermNameMapQuery,
} from 'store/api/authApi';
import {
    usePostNewRequestMutation,
    usePostUpdateRequestDetailsMutation,
} from 'store/api/payerRequestApi';
import { type PayerSegment, useGetSegmentsQuery } from 'store/api/referenceApi';
import { useGetTreatmentsForPatientQuery } from 'store/api/treatmentApi';

const UNASSIGNED = 'Unassigned';

const hideAdditionalInfoSection = ({
    requestType,
    requestSubType,
}: Pick<RequestDetails, 'requestType' | 'requestSubType' | 'segmentId'>) =>
    !requestType || (requestType === RequestType.CHANGE && !requestSubType);

const isProductChangeRequest = ({
    requestType,
    requestSubType,
}: Pick<RequestDetails, 'requestType' | 'requestSubType'>) =>
    requestType === RequestType.CHANGE && requestSubType === ChangeRequestSubType.PRODUCT;

const ChangeRequestSubTypeDisplayTextMap = {
    [ChangeRequestSubType.PHARMACY]: 'pharmacy',
    [ChangeRequestSubType.PRODUCT]: 'product',
} as const;

const useDefaultSegmentOption = (segmentOptions?: PayerSegment[]) => {
    const { payerId, segmentId } = useAppSelector((state) => state.views);

    // If segment is selected in views, use that segment
    if (segmentId) {
        return { payerId, segmentId };
    }

    // If there is only one segment option, select it
    if (segmentOptions?.length === 1) {
        return {
            payerId: segmentOptions[0].payerId,
            segmentId: segmentOptions[0].value,
        };
    }

    // Otherwise, force user to select segment
    return undefined;
};

const isNewRequest = (requestDetails: RequestDetails | undefined) => !requestDetails;

interface SubmitRequestDialogProps {
    title: string;
    isOpen: boolean;
    payerId?: string;
    onClose: () => void;
    requestDetails?: RequestDetails;
}

export const SubmitRequestDialog = ({
    title,
    isOpen,
    payerId = undefined,
    onClose,
    requestDetails = undefined,
}: SubmitRequestDialogProps) => {
    const { user } = useAuth0();
    const navigate = useNavigate();
    const [postNewRequest] = usePostNewRequestMutation();
    const { showPayerRequestSubSubTypes } = useFlags<{ showPayerRequestSubSubTypes: boolean }>();
    const { data: compassUsers, isFetching: compassUsersFetching } =
        useGetCompassUserNameMapQuery();
    const [postUpdateRequestDetails] = usePostUpdateRequestDetailsMutation();

    const [formSegmentId, setFormSegmentId] = useState<string>();
    const [formRequestType, setFormRequestType] = useState<string>();
    const [switchedToPBMOnly, setSwitchedToPBMOnly] = useState<boolean>(false);

    useEffect(() => {
        if (!isNewRequest(requestDetails)) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setFormSegmentId(requestDetails.segmentId);
            setFormRequestType(requestDetails.requestType);
        }
    }, [requestDetails]);

    const internalAssigneeOptions = [
        { value: UNASSIGNED, name: UNASSIGNED },
        { value: user?.email, name: 'Assign to Me' },
        ...Object.entries({ ...compassUsers })
            .filter(([upn]) => upn !== user?.email)
            .map(([upn, name]) => ({ value: upn, name })),
    ];

    const { data: allUsers, isFetching: usersWithPermFetching } =
        useGetPayerUserWithPermNameMapQuery(
            formSegmentId && formRequestType
                ? {
                      segmentId: formSegmentId,
                      permission: `write:payer_request_${formRequestType.toLowerCase()}`,
                  }
                : skipToken,
        );

    const payerAssigneeOptions = [
        { value: UNASSIGNED, name: UNASSIGNED, hasPBMOnlyAccess: true },
        ...Object.entries({ ...allUsers })
            .filter(([upn]) => !upn.endsWith('@freemarkethealth.com'))
            .map(([upn, { full_name: fullName, has_pbm_only_access: hasPBMOnlyAccess }]) => ({
                value: upn,
                name: fullName,
                hasPBMOnlyAccess,
            })),
    ];

    const pbmUsersPresent =
        payerAssigneeOptions.filter(
            (option) => option.value !== UNASSIGNED && option.hasPBMOnlyAccess,
        ).length > 0;

    const { permissions, segmentIds } = useAppSelector((state) => state.auth);
    const { data: segments, isFetching: isFetchingSegments } = useGetSegmentsQuery();

    // Only show segments for currently selected payer
    const segmentOptions = (segments ?? []).filter(
        (segment) =>
            segmentIds.includes(segment.segmentId) && (!payerId || payerId === segment.payerId),
    );

    const { data: treatments } = useGetTreatmentsForPatientQuery(
        payerId &&
            requestDetails?.memberId &&
            requestDetails.firstName &&
            requestDetails.lastName &&
            requestDetails.dateOfBirth
            ? {
                  payerId,
                  segmentId: requestDetails.segmentId,
                  memberId: requestDetails.memberId,
                  patientFirstName: requestDetails.firstName,
                  patientLastName: requestDetails.lastName,
                  patientDob: requestDetails.dateOfBirth,
              }
            : skipToken,
    );

    const defaultSegmentOption = useDefaultSegmentOption(segmentOptions);

    const requestTypeOptions = useMemo(
        () =>
            Object.entries(ReadPermissionRequestTypeMap)
                .filter(([permission]) => permissions.includes(permission))
                .map(([_, value]) => ({
                    value,
                    name: RequestTypeDisplayTextMap[value],
                })),
        [permissions],
    );

    const validationSchema = Yup.object({
        payerId: Yup.string().required(validationStrings.requiredField.errorMessage),
        ...MemberEscalation.validationSchema(showPayerRequestSubSubTypes),
        requestSubType: Yup.string().when('requestType', {
            is: (requestType: RequestDetails['requestType']) =>
                (
                    [RequestType.ESCALATION, RequestType.CHANGE] as RequestDetails['requestType'][]
                ).includes(requestType),
            then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
        }),
        additionalNotes: Yup.string().when(['requestType', 'requestSubType', 'typeLevel2'], {
            is: (
                requestType: RequestDetails['requestType'],
                requestSubType: RequestDetails['requestSubType'],
                typeLevel2: RequestDetails['typeLevel2'],
            ) =>
                requestType === RequestType.OTHER ||
                (requestType === RequestType.CHANGE &&
                    requestSubType !== ChangeRequestSubType.PRODUCT) ||
                (requestType === RequestType.ESCALATION &&
                    requestSubType === 'Resolve Claim Rejection' &&
                    typeLevel2 === 'Other'),
            then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
        }),
        requiredFile: Yup.array().when(['requestType', 'requestSubType'], {
            is: (
                requestType: RequestDetails['requestType'],
                requestSubType: RequestDetails['requestSubType'],
            ) =>
                isProductChangeRequest({ requestType, requestSubType }) &&
                // Not required when updating details for existing product change requests
                (isNewRequest(requestDetails) || !isProductChangeRequest(requestDetails)),
            then: (schema) =>
                schema
                    .required(validationStrings.requiredField.errorMessage)
                    .length(1, validationStrings.requiredField.errorMessage),
        }),
        requiredFileUpload: Yup.array(),
        additionalFiles: Yup.array(),
        additionalFilesUpload: Yup.array(),
        urgent: Yup.bool(),
        pbmOnly: Yup.string(),
        assignedUpn: Yup.string(),
        payerAssignedUpn: Yup.string().when(['pbmOnly'], {
            is: (pbmOnly: string) => pbmOnly === 'true',
            then: (schema) =>
                schema.notOneOf(
                    payerAssigneeOptions
                        .filter((option) => !option.hasPBMOnlyAccess)
                        .map((option) => option.value),
                    'Not a PBM user.',
                ),
        }),
    });

    return (
        <Formik
            initialValues={
                (!isNewRequest(requestDetails)
                    ? {
                          // Strip the null values from the existing request to avoid formik errors
                          ...Object.fromEntries(
                              Object.entries(requestDetails).filter(([_, v]) => v != null),
                          ),
                          payerId,
                          treatments,
                          selectedTreatments: requestDetails.treatmentIds,
                          pbmOnly: requestDetails.pbmOnly ? 'true' : 'false',
                      }
                    : {
                          ...defaultSegmentOption,
                          pbmOnly: pbmUsersPresent ? 'true' : 'false',
                      }) as Yup.InferType<typeof validationSchema>
            }
            enableReinitialize
            validateOnMount
            validationSchema={validationSchema}
            onSubmit={async (values, { resetForm }) => {
                const body = {
                    payerId: values.payerId,
                    segmentId: values.segmentId,
                    requestType: values.requestType,
                    requestSubType: values.requestSubType,
                    typeLevel2: values.typeLevel2,
                    memberId: values.memberId,
                    firstName: values.firstName,
                    lastName: values.lastName,
                    dateOfBirth: values.dateOfBirth
                        ? formatDate(values.dateOfBirth, DateFormats.HYPHEN_DATE)
                        : undefined,
                    treatmentIds: values.selectedTreatments ?? [],
                    additionalNotes: values.additionalNotes ?? undefined,
                    files: [
                        ...((values.requiredFileUpload ?? []) as File[]),
                        ...((values.additionalFilesUpload ?? []) as File[]),
                    ],
                    urgent: values.urgent ?? false,
                    pbmOnly: values.pbmOnly === 'true',
                    assignedUpn: values.assignedUpn,
                    payerAssignedUpn: values.payerAssignedUpn,
                };

                if (isNewRequest(requestDetails)) {
                    const newRequestId = await postNewRequest(body).unwrap();
                    SuccessToast({ message: 'Request created successfully.' });
                    navigate(AppRoutes.requestDetails(values.payerId, newRequestId));
                } else {
                    await postUpdateRequestDetails({
                        ...body,
                        requestId: requestDetails.requestId,
                    }).unwrap();
                    SuccessToast({ message: 'Request details updated successfully.' });
                }
                resetForm();
                onClose();
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle={title}
                    isOpen={isOpen}
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        onClose();
                        formik.resetForm();
                    }}
                    primaryButtonLabel={title}
                    isPrimaryButtonDisabled={
                        !(formik.dirty && formik.isValid) || formik.isSubmitting
                    }
                    dialogContent={
                        <>
                            {!isNewRequest(requestDetails) && (
                                <div className="margin__bottom--medium">
                                    <InlineMessage
                                        intent={InlineMessage.Intent.INFO}
                                        label="Updating the request type may place this request in a different category and may require additional information to be provided."
                                        wrapText
                                    />
                                </div>
                            )}
                            {segmentIds.length !== 1 && (
                                <SuggestSingleSelectFormik
                                    id="segmentId"
                                    label="Business Segment"
                                    required
                                    disableAutoPopulate
                                    isLoading={isFetchingSegments}
                                    values={segmentOptions}
                                    selectedItem={segmentOptions.find(
                                        ({ value }) => value === formik.values.segmentId,
                                    )}
                                    onChange={(newVal) => {
                                        (() =>
                                            formik.setValues({
                                                payerId: newVal?.payerId,
                                                segmentId: newVal?.value,
                                                pbmOnly: formik.values.pbmOnly,
                                            } as Yup.InferType<typeof validationSchema>))();
                                        if (newVal) {
                                            setFormSegmentId(newVal.value);
                                        }
                                        if (!isNewRequest(requestDetails)) {
                                            (async () => {
                                                await formik.setFieldValue(
                                                    'assignedUpn',
                                                    UNASSIGNED,
                                                );
                                                await formik.setFieldValue(
                                                    'payerAssignedUpn',
                                                    UNASSIGNED,
                                                );
                                            })();
                                        }
                                    }}
                                />
                            )}
                            <SuggestSingleSelectFormik
                                id="requestType"
                                label="What Can We Help You With?"
                                required
                                values={requestTypeOptions}
                                selectedItem={requestTypeOptions.find(
                                    ({ value }) => value === formik.values.requestType,
                                )}
                                onChange={(newVal) => {
                                    (() =>
                                        formik.setValues({
                                            payerId: formik.values.payerId,
                                            segmentId: formik.values.segmentId,
                                            requestType: newVal?.value,
                                            pbmOnly: formik.values.pbmOnly,
                                        } as Yup.InferType<typeof validationSchema>))();
                                    if (newVal) {
                                        setFormRequestType(newVal.value);
                                    }
                                    if (!isNewRequest(requestDetails)) {
                                        (async () => {
                                            await formik.setFieldValue('assignedUpn', UNASSIGNED);
                                            await formik.setFieldValue(
                                                'payerAssignedUpn',
                                                UNASSIGNED,
                                            );
                                        })();
                                    }
                                }}
                            />
                            <MemberEscalation
                                payerId={formik.values.payerId}
                                requestDetailsPBMOnly={requestDetails?.pbmOnly}
                                isNewRequest={isNewRequest(requestDetails)}
                                pbmUsersPresent={pbmUsersPresent}
                            />
                            {formik.values.requestType === RequestType.CHANGE && (
                                <>
                                    <SuggestSingleSelectFormik
                                        id="requestSubType"
                                        label="Please Select One Of The Following"
                                        values={Object.values(ChangeRequestSubType)}
                                        required
                                        onChange={(newVal) => {
                                            (() =>
                                                formik.setValues({
                                                    payerId: formik.values.payerId,
                                                    segmentId: formik.values.segmentId,
                                                    requestType: formik.values.requestType,
                                                    requestSubType: newVal,
                                                    pbmOnly: formik.values.pbmOnly,
                                                }))();
                                            if (!isNewRequest(requestDetails)) {
                                                (async () => {
                                                    await formik.setFieldValue(
                                                        'assignedUpn',
                                                        UNASSIGNED,
                                                    );
                                                    await formik.setFieldValue(
                                                        'payerAssignedUpn',
                                                        UNASSIGNED,
                                                    );
                                                })();
                                            }
                                        }}
                                    />
                                    {pbmUsersPresent && (
                                        <PBMOnlySelector
                                            onChange={(newVal) => {
                                                (() =>
                                                    formik.setFieldTouched(
                                                        'payerAssignedUpn',
                                                        true,
                                                        true,
                                                    ))();
                                                (() => formik.setFieldValue('pbmOnly', newVal))();
                                                if (!isNewRequest(requestDetails)) {
                                                    setSwitchedToPBMOnly(
                                                        newVal === 'true' &&
                                                            !requestDetails?.pbmOnly,
                                                    );
                                                }
                                            }}
                                        />
                                    )}
                                    {switchedToPBMOnly && (
                                        <div className="margin__bottom--medium margin__top--xx-small">
                                            <InlineMessage
                                                intent={InlineMessage.Intent.WARNING}
                                                label="Non-PBM users may have already viewed the contents of this request and/or have received notifications regarding it."
                                                wrapText
                                            />
                                        </div>
                                    )}
                                    {(() => {
                                        if (!formik.values.requestSubType) {
                                            return null;
                                        }
                                        return isProductChangeRequest(formik.values) ? (
                                            <>
                                                <div className="sds_label font-semi-bold--uppercase margin__bottom--medium">
                                                    {`${
                                                        ChangeRequestSubTypeDisplayTextMap[
                                                            formik.values
                                                                .requestSubType as keyof typeof ChangeRequestSubTypeDisplayTextMap
                                                        ]
                                                    } Information`}
                                                </div>
                                                <FileUploadFormik
                                                    id="requiredFile"
                                                    label="Upload Product File"
                                                    // File upload not required if the existing request is a product change request
                                                    required={
                                                        isNewRequest(requestDetails) ||
                                                        !isProductChangeRequest(requestDetails)
                                                    }
                                                    accept="application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                                                    maxAllowedFiles={1}
                                                    onAddFiles={(files) =>
                                                        formik.setFieldValue(
                                                            'requiredFileUpload',
                                                            files,
                                                        )
                                                    }
                                                    onRemoveFile={() =>
                                                        formik.setFieldValue(
                                                            'requiredFileUpload',
                                                            undefined,
                                                        )
                                                    }
                                                    helperText={
                                                        <div
                                                            className="flex__gap--small"
                                                            style={{ flexDirection: 'column' }}
                                                        >
                                                            <div>
                                                                Please use the template file below
                                                                to upload product information.
                                                            </div>
                                                            <Hyperlink
                                                                navUrl="https://sseasacdn.blob.core.windows.net/platform-assets/Product-Management-Template.xlsx"
                                                                linkText={
                                                                    <div className="flex__gap--small">
                                                                        <span>
                                                                            Product-Management-Template.xlsx
                                                                        </span>
                                                                        <Icon
                                                                            icon={
                                                                                IconNames.DOWNLOAD
                                                                            }
                                                                        />
                                                                    </div>
                                                                }
                                                            />
                                                        </div>
                                                    }
                                                />
                                            </>
                                        ) : (
                                            <TextAreaFormik
                                                id="additionalNotes"
                                                label="Please Describe The Change Required"
                                                required
                                            />
                                        );
                                    })()}
                                </>
                            )}
                            {formik.values.requestType === RequestType.OTHER && pbmUsersPresent && (
                                <PBMOnlySelector
                                    onChange={(newVal) => {
                                        (() =>
                                            formik.setFieldTouched(
                                                'payerAssignedUpn',
                                                true,
                                                true,
                                            ))();
                                        (() => formik.setFieldValue('pbmOnly', newVal))();
                                        if (!isNewRequest(requestDetails)) {
                                            setSwitchedToPBMOnly(
                                                newVal === 'true' && !requestDetails?.pbmOnly,
                                            );
                                        }
                                    }}
                                />
                            )}
                            {formik.values.requestType === RequestType.OTHER &&
                                switchedToPBMOnly && (
                                    <div className="margin__bottom--medium margin__top--xx-small">
                                        <InlineMessage
                                            intent={InlineMessage.Intent.WARNING}
                                            label="Non-PBM users may have already viewed the contents of this request and/or have received notifications regarding it."
                                            wrapText
                                        />
                                    </div>
                                )}
                            {formik.values.requestType === RequestType.OTHER && (
                                <TextAreaFormik
                                    id="additionalNotes"
                                    label="Please Describe The Request"
                                    required
                                />
                            )}
                            {!hideAdditionalInfoSection(formik.values) &&
                                !usersWithPermFetching && (
                                    <>
                                        <div className="sds_label font-semi-bold--uppercase margin__bottom--medium">
                                            Assignment
                                        </div>
                                        <SuggestSingleSelectFormik
                                            id="assignedUpn"
                                            label="FMH Assignee"
                                            isLoading={compassUsersFetching}
                                            values={internalAssigneeOptions}
                                            selectedItem={internalAssigneeOptions.find(
                                                ({ value }) => value === formik.values.assignedUpn,
                                            )}
                                            onChange={(newVal) => {
                                                (() =>
                                                    formik.setFieldValue(
                                                        'assignedUpn',
                                                        newVal?.value,
                                                    ))();
                                            }}
                                        />
                                        <SuggestSingleSelectFormik
                                            id="payerAssignedUpn"
                                            label="Payer Assignee"
                                            isLoading={usersWithPermFetching}
                                            values={payerAssigneeOptions.filter((option) =>
                                                formik.values.pbmOnly === 'true'
                                                    ? option?.hasPBMOnlyAccess
                                                    : true,
                                            )}
                                            selectedItem={payerAssigneeOptions.find(
                                                ({ value }) =>
                                                    value === formik.values.payerAssignedUpn,
                                            )}
                                            onChange={(newVal) => {
                                                (() =>
                                                    formik.setFieldValue(
                                                        'payerAssignedUpn',
                                                        newVal?.value,
                                                    ))();
                                            }}
                                        />
                                        <div className="sds_label font-semi-bold--uppercase margin__bottom--medium">
                                            Additional Information
                                        </div>
                                        {(formik.values.requestType === RequestType.ESCALATION ||
                                            isProductChangeRequest(formik.values)) && (
                                            <TextAreaFormik
                                                id="additionalNotes"
                                                label="Anything Else You Would Like Us To Know?"
                                                required={
                                                    formik.values.requestSubType ===
                                                        'Resolve Claim Rejection' &&
                                                    formik.values.typeLevel2 === 'Other'
                                                }
                                            />
                                        )}
                                        <FileUploadFormik
                                            id="additionalFiles"
                                            label="Attach File(s)"
                                            accept="application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/csv,application/pdf"
                                            helperText="Accepted file types: xlsx, docx, csv, and pdf"
                                            onAddFiles={(files) =>
                                                formik.setFieldValue('additionalFilesUpload', [
                                                    ...((formik.values.additionalFilesUpload ??
                                                        []) as File[]),
                                                    ...files,
                                                ])
                                            }
                                            onRemoveFile={(filename) =>
                                                formik.setFieldValue(
                                                    'additionalFilesUpload',
                                                    formik.values.additionalFilesUpload?.filter(
                                                        (file: File) => file.name !== filename,
                                                    ),
                                                )
                                            }
                                        />
                                        <CheckboxListFormik
                                            name="urgent"
                                            label="Request Urgency"
                                            options={['Urgent']}
                                            values={formik.values.urgent ? ['Urgent'] : []}
                                            onChange={(newValue) => {
                                                (() =>
                                                    formik.setFieldValue(
                                                        'urgent',
                                                        newValue.includes('Urgent'),
                                                    ))();
                                            }}
                                        />
                                    </>
                                )}
                        </>
                    }
                />
            )}
        </Formik>
    );
};
